import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Clock, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXPECTED_MINUTES = 10;

const STEPS = [
  { pct: 8, label: 'Researching routes & transfers' },
  { pct: 32, label: 'Curating stays for every night' },
  { pct: 58, label: 'Unlocking local hidden gems' },
  { pct: 82, label: 'Finalising your PDF voucher' },
];

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const GeneratingScreen = ({ startTime, quotes = [], destinations = [] }) => {
  const [elapsed, setElapsed] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [slide, setSlide] = useState(0);
  const [quoteIndex, setQuoteIndex] = useState(0);

  const query = useMemo(
    () => (destinations.filter(Boolean)[0] || 'india travel destination').toString(),
    [destinations]
  );

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
    return () => clearInterval(t);
  }, [startTime]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/destination-photos?q=${encodeURIComponent(query)}&count=5`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d.photos?.length) setPhotos(d.photos); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [query]);

  useEffect(() => {
    if (photos.length < 2) return;
    const t = setInterval(() => setSlide((p) => (p + 1) % photos.length), 7000);
    return () => clearInterval(t);
  }, [photos.length]);

  useEffect(() => {
    if (quotes.length < 2) return;
    const t = setInterval(() => setQuoteIndex((p) => (p + 1) % quotes.length), 20000);
    return () => clearInterval(t);
  }, [quotes.length]);

  const total = EXPECTED_MINUTES * 60;
  const overrun = elapsed >= total;
  // Honest overrun: never show 100%, never claim it finished. Hold at 99% and switch to elapsed.
  const pct = overrun ? 99 : Math.min(99, (elapsed / total) * 100);
  const remaining = Math.max(0, total - elapsed);
  const quote = quotes[quoteIndex];

  return (
    <div className="relative min-h-[82vh] overflow-hidden rounded-3xl" data-testid="generating-screen">
      {photos.map((src, i) => (
        <div
          key={src}
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center transition-opacity"
          style={{ backgroundImage: `url(${src})`, opacity: i === slide ? 1 : 0, transitionDuration: '2000ms' }}
        />
      ))}
      <div className="absolute inset-0 bg-slate-950/75" aria-hidden="true" />
      {!photos.length && <div className="absolute inset-0 bg-slate-950" aria-hidden="true" />}

      <div className="relative z-10 flex min-h-[82vh] items-center justify-center px-5 py-16">
        <div className="w-full max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-5">
            NavMind · Crafting your journey
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-white leading-tight mb-3">
            {overrun ? 'Almost there — adding the final polish' : `Designing your ${query} itinerary`}
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-10 max-w-md">
            {overrun
              ? 'This one is taking a little longer than usual. Nothing has gone wrong — your itinerary is still being written and will appear here automatically.'
              : 'Reading live routes, stays and local recommendations for every day of your trip.'}
          </p>

          <div className="mb-3 flex items-baseline justify-between">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
              {overrun ? 'Elapsed' : 'Time remaining'}
            </span>
            <span
              className="font-mono text-2xl font-bold text-amber-400 tabular-nums"
              data-testid="waiting-countdown-timer"
            >
              {overrun ? `+${fmt(elapsed - total)}` : fmt(remaining)}
            </span>
          </div>

          <div
            className="relative h-1.5 w-full overflow-hidden rounded-full bg-white/15"
            role="progressbar"
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
            data-testid="waiting-progress-bar"
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-[width] duration-1000"
              style={{ width: `${pct}%` }}
            />
            {overrun && (
              <div className="absolute inset-0 animate-marquee bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            )}
          </div>
          <p className="mt-2 font-mono text-[11px] text-slate-400" data-testid="waiting-progress-label">
            {overrun ? '99% · still working' : `${Math.round(pct)}%`}
          </p>

          <ul className="mt-10 space-y-3">
            {STEPS.map((s) => {
              const done = pct > s.pct + 20;
              const active = !done && pct > s.pct;
              return (
                <li key={s.label} className="flex items-center gap-3 text-sm">
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                      done
                        ? 'border-emerald-400/40 bg-emerald-400/15 text-emerald-300'
                        : active
                        ? 'border-amber-400/40 bg-amber-400/15 text-amber-300'
                        : 'border-white/15 bg-white/5 text-slate-500'
                    }`}
                  >
                    {done ? <Check className="h-3.5 w-3.5" />
                      : active ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Clock className="h-3 w-3" />}
                  </span>
                  <span className={done ? 'text-slate-300' : active ? 'text-white' : 'text-slate-500'}>
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ul>

          {quote && (
            <figure className="mt-10 border-l-2 border-amber-400/50 pl-5">
              <blockquote className="font-serif text-lg italic leading-relaxed text-slate-200">
                &ldquo;{quote.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/80">
                {quote.author}
              </figcaption>
            </figure>
          )}

          <p className="mt-10 flex items-start gap-2 text-xs leading-relaxed text-slate-400">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400/70" />
            Please stay on this page — your itinerary loads automatically, and we email it to you as well.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GeneratingScreen;
